// Estado de navegación del workspace de un pedido, en la URL.
//
// Vive en la query y no en `useState` para que refrescar no devuelva al usuario
// al resumen, y para que se pueda pasar un enlace a «las líneas facturadas de la
// factura 26AP2000080» y llegue justo ahí. Es también lo que permite que «Ver
// sus líneas» de Facturación cambie de pestaña, de grupo y de filtro en un solo
// gesto, sin que Facturación conozca los interiores de Líneas.
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const TABS = ['resumen', 'cargas', 'lineas', 'facturacion', 'documentos'] as const
export type TabPedido = typeof TABS[number]

// 'todas' es la vista agrupada por LÍNEA; las otras dos van al grano
// (línea × factura) que devuelve la vista. Van en el mismo parámetro porque son
// tres formas de mirar lo mismo y sólo una está activa.
export const GRUPOS = ['pendientes', 'facturadas', 'todas'] as const
export type GrupoLineas = typeof GRUPOS[number]

export interface EstadoTabs {
  tab: TabPedido
  grupo: GrupoLineas
  /** Facturas por las que filtrar. Vacío = todas. En la URL, separadas por coma. */
  facturas: string[]
  /** Los demás filtros de la sección de líneas. Viven en la URL y no en la sección
   *  para que sobrevivan al cambio de pestaña —que la desmonta— y para que el
   *  enlace lleve a lo que se está mirando. */
  articulos: string[]
  albaranes: string[]
  cargas: string[]
  destinos: string[]
  conCarga: FiltroConCarga
  familia: string
  estado: string
  irA: (tab: TabPedido) => void
  setGrupo: (grupo: GrupoLineas) => void
  setFacturas: (facturas: string[]) => void
  setArticulos: (articulos: string[]) => void
  setAlbaranes: (albaranes: string[]) => void
  setCargas: (cargas: string[]) => void
  setDestinos: (destinos: string[]) => void
  setConCarga: (v: FiltroConCarga) => void
  setFamilia: (familia: string) => void
  setEstado: (estado: string) => void
  limpiarFiltros: () => void
  /** El salto de Facturación a las líneas de una factura concreta. */
  verLineasDeFactura: (factura: string) => void
}

function comoTab(valor: string | null): TabPedido {
  return TABS.includes(valor as TabPedido) ? (valor as TabPedido) : 'resumen'
}

// Por defecto «todas»: es la vista que contesta la pregunta con la que se entra
// —qué hay en este pedido— y desde la que se baja al detalle. Antes entraba por
// «pendientes», que es un subconjunto, y el usuario no tenía forma de saber que
// estaba viendo una parte.
function comoGrupo(valor: string | null): GrupoLineas {
  return GRUPOS.includes(valor as GrupoLineas) ? (valor as GrupoLineas) : 'todas'
}

/** Tres estados: sin filtrar, sólo lo que salió por una carga, sólo lo que no.
 *  El tercero es el que aísla las entregas manuales y los artículos no
 *  almacenables, que hoy no se pueden separar de un vistazo. */
export type FiltroConCarga = '' | 'con' | 'sin'

function comoConCarga(valor: string | null): FiltroConCarga {
  return valor === 'con' || valor === 'sin' ? valor : ''
}

/** Lista separada por comas de la URL. Vacía cuando no hay parámetro. */
function comoLista(valor: string | null): string[] {
  return (valor ?? '').split(',').map(v => v.trim()).filter(Boolean)
}

export function useTabsPedido(): EstadoTabs {
  const [params, setParams] = useSearchParams()

  const tab = comoTab(params.get('tab'))
  const grupo = comoGrupo(params.get('grupo'))
  const facturas = useMemo(() => comoLista(params.get('facturas')), [params])
  const articulos = useMemo(() => comoLista(params.get('articulos')), [params])
  const albaranes = useMemo(() => comoLista(params.get('albaranes')), [params])
  const cargasFiltro = useMemo(() => comoLista(params.get('cargas')), [params])
  const destinos = useMemo(() => comoLista(params.get('destinos')), [params])
  const conCarga = comoConCarga(params.get('conCarga'))
  const familia = params.get('familia') ?? ''
  const estado = params.get('estado') ?? ''

  // `replace` en todos los casos: dentro de un mismo pedido, cambiar de pestaña
  // no debería llenar el historial de pasos que el botón «atrás» tiene que
  // deshacer uno a uno antes de volver a la lista.
  const escribir = useCallback((patch: Record<string, string>) => {
    setParams(prev => {
      const p = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(patch)) {
        if (v) p.set(k, v); else p.delete(k)
      }
      return p
    }, { replace: true })
  }, [setParams])

  return useMemo(() => ({
    tab,
    grupo,
    facturas,
    articulos,
    albaranes,
    cargas: cargasFiltro,
    destinos,
    conCarga,
    familia,
    estado,
    // Cambiar de pestaña NO borra los filtros. Antes sí: la idea era que volver a
    // Líneas diera la vista por defecto en vez de los filtros de hace media hora.
    // En uso real es al revés —se va a Cargas a comprobar algo de ESE artículo y se
    // vuelve—, y rehacer el filtro cada vez era el coste de una idea razonable que
    // el uso desmintió.
    irA: (destino) => escribir({ tab: destino === 'resumen' ? '' : destino }),
    setGrupo: (g) => escribir({ grupo: g === 'todas' ? '' : g }),
    setFacturas: (fs) => escribir({ facturas: fs.join(',') }),
    setArticulos: (a) => escribir({ articulos: a.join(',') }),
    setAlbaranes: (a) => escribir({ albaranes: a.join(',') }),
    setCargas: (c) => escribir({ cargas: c.join(',') }),
    setDestinos: (d) => escribir({ destinos: d.join(',') }),
    setConCarga: (v) => escribir({ conCarga: v }),
    setFamilia: (f) => escribir({ familia: f }),
    setEstado: (e) => escribir({ estado: e }),
    limpiarFiltros: () => escribir({
      articulos: '', albaranes: '', cargas: '', destinos: '', conCarga: '',
      familia: '', estado: '', facturas: '',
    }),
    // SUSTITUYE la selección en vez de añadir: «ver sus líneas» de una factura
    // concreta tiene que llevar a ESA factura, no a la de antes más esta.
    verLineasDeFactura: (f) => escribir({ tab: 'lineas', grupo: 'facturadas', facturas: f }),
  }), [tab, grupo, facturas, articulos, albaranes, cargasFiltro, destinos, conCarga,
       familia, estado, escribir])
}
